/**
 * ProductAssignmentStep
 *
 * Allows users to assign products to briefs before generation.
 * Users can either manually assign products or use random assignment.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Package,
    Shuffle,
    ArrowRight,
    ArrowLeft,
    Check,
    AlertCircle,
    Image as ImageIcon
} from 'lucide-react';

const ProductAssignmentStep = ({
    plan,
    pillars,
    products,
    assignments,
    onAssignmentsChange,
    onNext,
    onBack
}) => {
    const [assignmentMode, setAssignmentMode] = useState('random'); // 'random' | 'manual'

    // Get active products
    const activeProducts = useMemo(() =>
        products.filter(p => p.isActive),
        [products]
    );

    // Calculate brief slots based on plan recommendations
    const briefSlots = useMemo(() => {
        if (!plan?.recommendations?.byPillar) return [];

        const slots = [];
        const funnelStages = ['TOFU', 'MOFU', 'BOFU'];

        Object.entries(plan.recommendations.byPillar).forEach(([pillarId, pillarData]) => {
            const pillar = pillars.find(p => p.id === pillarId);
            if (!pillar) return;

            // Distribute this pillar's briefs across funnel stages
            const totalForPillar = pillarData.recommended || 0;
            const distribution = plan.recommendations.byFunnelStage || {
                TOFU: { percentage: 40 },
                MOFU: { percentage: 35 },
                BOFU: { percentage: 25 }
            };

            funnelStages.forEach(stage => {
                const stagePercent = distribution[stage]?.percentage || 33;
                const countForStage = Math.round(totalForPillar * (stagePercent / 100));

                for (let i = 0; i < countForStage; i++) {
                    slots.push({
                        id: `${pillarId}-${stage}-${i}`,
                        pillarId,
                        pillarName: pillar.name,
                        pillarColor: pillar.color,
                        funnelStage: stage,
                        productId: null
                    });
                }
            });
        });

        return slots;
    }, [plan, pillars]);

    // Initialize assignments from slots
    useEffect(() => {
        if (briefSlots.length > 0 && assignments.length === 0) {
            // Auto-assign randomly on first load
            handleRandomAssign();
        }
    }, [briefSlots]);

    // Randomly assign products to all slots
    const handleRandomAssign = () => {
        if (activeProducts.length === 0) return;

        const newAssignments = briefSlots.map(slot => ({
            ...slot,
            productId: activeProducts[Math.floor(Math.random() * activeProducts.length)].id
        }));

        onAssignmentsChange(newAssignments);
    };

    // Update a single assignment
    const handleAssignmentChange = (slotId, productId) => {
        const updated = assignments.map(a =>
            a.id === slotId ? { ...a, productId } : a
        );
        onAssignmentsChange(updated);
    };

    // Get product by ID
    const getProduct = (productId) => products.find(p => p.id === productId);

    // Group assignments by pillar for display
    const groupedByPillar = useMemo(() => {
        const grouped = {};
        assignments.forEach(a => {
            if (!grouped[a.pillarId]) {
                grouped[a.pillarId] = {
                    pillarName: a.pillarName,
                    pillarColor: a.pillarColor,
                    slots: []
                };
            }
            grouped[a.pillarId].slots.push(a);
        });
        return grouped;
    }, [assignments]);

    const hasProducts = activeProducts.length > 0;
    const allAssigned = assignments.length > 0 && assignments.every(a => a.productId);

    if (!hasProducts) {
        return (
            <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                <div style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto var(--space-4)',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--warning-50)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Package size={40} style={{ color: 'var(--warning-600)' }} />
                </div>
                <h3>No Products Available</h3>
                <p style={{ color: 'var(--color-text-secondary)', maxWidth: '400px', margin: '0 auto var(--space-4)' }}>
                    You haven't added any products yet. Add products in the Strategy Hub to link them to your content briefs.
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center' }}>
                    <button onClick={onBack} className="btn btn-ghost">
                        <ArrowLeft size={16} />
                        Back
                    </button>
                    <button onClick={onNext} className="btn btn-primary">
                        Skip & Continue
                        <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: 'var(--space-4)' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-4)'
            }}>
                <div>
                    <h3 style={{ margin: 0 }}>Assign Products to Briefs</h3>
                    <p style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0 0', fontSize: 'var(--font-size-sm)' }}>
                        Each brief will be generated for its assigned product. {assignments.length} briefs to assign.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button
                        onClick={handleRandomAssign}
                        className="btn btn-secondary"
                        title="Randomly assign products to all briefs"
                    >
                        <Shuffle size={16} />
                        Randomize All
                    </button>
                </div>
            </div>

            {/* Assignment Mode Toggle */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-4)',
                padding: 'var(--space-1)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                width: 'fit-content'
            }}>
                <button
                    onClick={() => setAssignmentMode('random')}
                    className={`btn btn-sm ${assignmentMode === 'random' ? 'btn-primary' : 'btn-ghost'}`}
                >
                    <Shuffle size={14} />
                    Random
                </button>
                <button
                    onClick={() => setAssignmentMode('manual')}
                    className={`btn btn-sm ${assignmentMode === 'manual' ? 'btn-primary' : 'btn-ghost'}`}
                >
                    <Package size={14} />
                    Manual
                </button>
            </div>

            {assignmentMode === 'random' ? (
                /* Random Mode - Show summary */
                <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                        <Check size={20} style={{ color: 'var(--success-600)' }} />
                        <span style={{ fontWeight: 500 }}>Products will be randomly assigned</span>
                    </div>
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                        Each brief will get a randomly selected product from your {activeProducts.length} active product{activeProducts.length !== 1 ? 's' : ''}.
                        Click "Randomize All" to shuffle the assignments.
                    </p>

                    {/* Product preview grid */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-2)',
                        marginTop: 'var(--space-3)'
                    }}>
                        {activeProducts.slice(0, 6).map(product => (
                            <div
                                key={product.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--font-size-sm)'
                                }}
                            >
                                {product.imageUrl ? (
                                    <img
                                        src={product.imageUrl}
                                        alt={product.name}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            objectFit: 'cover',
                                            borderRadius: 'var(--radius-sm)'
                                        }}
                                    />
                                ) : (
                                    <ImageIcon size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                                )}
                                <span>{product.name}</span>
                            </div>
                        ))}
                        {activeProducts.length > 6 && (
                            <div style={{
                                padding: 'var(--space-2)',
                                background: 'var(--color-bg-tertiary)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                +{activeProducts.length - 6} more
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* Manual Mode - Show assignment grid */
                <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    {Object.entries(groupedByPillar).map(([pillarId, { pillarName, pillarColor, slots }]) => (
                        <div key={pillarId} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <div style={{
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                fontWeight: 500,
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: 'var(--radius-full)',
                                    background: pillarColor || 'var(--color-primary)'
                                }} />
                                {pillarName} ({slots.length} briefs)
                            </div>
                            {slots.map(slot => {
                                const product = getProduct(slot.productId);
                                return (
                                    <div
                                        key={slot.id}
                                        style={{
                                            padding: 'var(--space-2) var(--space-3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 'var(--space-3)',
                                            borderTop: '1px solid var(--color-border)'
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            fontSize: 'var(--font-size-sm)'
                                        }}>
                                            <span style={{
                                                padding: '2px 8px',
                                                borderRadius: 'var(--radius-sm)',
                                                background: slot.funnelStage === 'TOFU' ? 'var(--info-100)' :
                                                           slot.funnelStage === 'MOFU' ? 'var(--warning-100)' : 'var(--success-100)',
                                                color: slot.funnelStage === 'TOFU' ? 'var(--info-700)' :
                                                       slot.funnelStage === 'MOFU' ? 'var(--warning-700)' : 'var(--success-700)',
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 500
                                            }}>
                                                {slot.funnelStage}
                                            </span>
                                        </div>
                                        <select
                                            value={slot.productId || ''}
                                            onChange={(e) => handleAssignmentChange(slot.id, e.target.value)}
                                            className="input"
                                            style={{
                                                flex: 1,
                                                maxWidth: '300px',
                                                fontSize: 'var(--font-size-sm)'
                                            }}
                                        >
                                            <option value="">Select a product...</option>
                                            {activeProducts.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {/* Footer */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 'var(--space-4)',
                paddingTop: 'var(--space-4)',
                borderTop: '1px solid var(--color-border)'
            }}>
                <button onClick={onBack} className="btn btn-ghost">
                    <ArrowLeft size={16} />
                    Back
                </button>
                <button
                    onClick={onNext}
                    className="btn btn-primary"
                    disabled={assignmentMode === 'manual' && !allAssigned}
                >
                    Continue to Generate
                    <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default ProductAssignmentStep;
